import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect, Suspense, lazy } from 'react';
import { supabase } from './lib/supabase';
import { SiteDataProvider } from './context/SiteDataContext';

// Layouts & public components (always needed)
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import SEOInjection from './components/SEOInjection';

// Public pages
import Home from './pages/Home';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import ResetPassword from './pages/ResetPassword';

// Admin — lazy loaded so public visitors never download this code
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const AdminLayout = lazy(() => import('./pages/AdminLayout'));
const DashboardHome = lazy(() => import('./pages/DashboardHome'));
const AdminProjects = lazy(() => import('./pages/AdminProjects'));
const AdminBlog = lazy(() => import('./pages/AdminBlog'));
const AdminEnquiries = lazy(() => import('./pages/AdminEnquiries'));
const AdminBranches = lazy(() => import('./pages/AdminBranches'));
const AdminClients = lazy(() => import('./pages/AdminClients'));
const AdminSettings = lazy(() => import('./pages/AdminSettings'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const AdminLogs = lazy(() => import('./pages/AdminLogs'));
const AdminTracker = lazy(() => import('./pages/AdminTracker'));
const AdminFinancials = lazy(() => import('./pages/AdminFinancials'));
const AdminSEO = lazy(() => import('./pages/AdminSEO'));
const PermissionGuard = lazy(() => import('./components/PermissionGuard'));

const AdminSpinner = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

function App() {
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 1. Initial Session Check
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    // 2. Listen for Auth Changes (including Password Recovery)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // 1. Role Enforcement
      if (session) {
        const role = (session.user.app_metadata as Record<string, string> | undefined)?.role;
        if (role !== "ADMIN" && role !== "MASTER_ADMIN") {
          supabase.auth.signOut();
          setSession(null);
          return;
        }
      }

      setSession(session);
      
      // 2. Password Recovery Handling
      if (event === 'PASSWORD_RECOVERY') {
        if (window.location.pathname !== '/reset-password') {
          window.location.href = '/reset-password';
        }
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <Router>
      <SiteDataProvider>
        <SEOInjection />
        <div className="min-h-screen bg-background flex flex-col">
          <Navbar />
          <main className="flex-grow">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* Admin Routes — loaded only when visited */}
              <Route path="/admin/login" element={
                <Suspense fallback={<AdminSpinner />}>
                  {loading ? null : !session ? <AdminLogin /> : <Navigate to="/admin/dashboard" />}
                </Suspense>
              } />

              <Route path="/admin" element={
                <Suspense fallback={<AdminSpinner />}>
                  {loading
                    ? <AdminSpinner />
                    : session ? <AdminLayout /> : <Navigate to="/admin/login" />}
                </Suspense>
              }>
                <Route index element={<Navigate to="/admin/dashboard" />} />
                <Route path="dashboard" element={<DashboardHome />} />
                <Route path="enquiries" element={<AdminEnquiries />} />
                <Route path="projects" element={<AdminProjects />} />
                <Route path="clients" element={<AdminClients />} />
                <Route path="branches" element={<AdminBranches />} />
                <Route path="blog" element={<AdminBlog />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="logs" element={<AdminLogs />} />
                <Route path="tracker"    element={<PermissionGuard module="calendar"><AdminTracker /></PermissionGuard>} />
                <Route path="financials" element={<PermissionGuard module="financials"><AdminFinancials /></PermissionGuard>} />
                <Route path="seo"        element={<PermissionGuard module="seo"><AdminSEO /></PermissionGuard>} />
              </Route>

              {/* Catch-all */}
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </main>
          <Footer />
        </div>
      </SiteDataProvider>
    </Router>
  );
}

export default App;
